import { C } from './palette';

/**
 * A tiny pixel-art format: rows of characters plus a colour key. Each sprite is
 * baked once into an offscreen canvas so drawing it later is a single drawImage.
 */
export interface Sprite {
  canvas: HTMLCanvasElement | OffscreenCanvas;
  w: number;
  h: number;
}

type Key = Record<string, string>;

function make2d(w: number, h: number): { c: HTMLCanvasElement | OffscreenCanvas; ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D } {
  if (typeof document !== 'undefined') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return { c, ctx: c.getContext('2d')! };
  }
  const c = new OffscreenCanvas(w, h);
  return { c, ctx: c.getContext('2d')! };
}

export function bake(rows: readonly string[], key: Key): Sprite {
  const h = rows.length;
  const w = Math.max(...rows.map((r) => r.length));
  const { c, ctx } = make2d(w, h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < rows[y].length; x++) {
      const ch = rows[y][x];
      const colour = key[ch];
      if (!colour) continue;
      ctx.fillStyle = colour;
      ctx.fillRect(x, y, 1, 1);
    }
  }
  return { canvas: c, w, h };
}

/** Horizontally mirrored copy, for the helicopter facing left. */
export function flip(s: Sprite): Sprite {
  const { c, ctx } = make2d(s.w, s.h);
  ctx.translate(s.w, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(s.canvas as CanvasImageSource, 0, 0);
  return { canvas: c, w: s.w, h: s.h };
}

// ---------------------------------------------------------------------------
// The sprites
// ---------------------------------------------------------------------------

const HELI_KEY: Key = {
  H: C.red,
  R: '#ff4444',
  K: '#3a0000',
  T: C.white,
  G: C.cyanB,
  M: C.slate,
};

/** Fuselage, nose to the right. The rotor and skids are drawn programmatically. */
export const HELI = bake(
  [
    '..........RRRR....',
    '......RRRRHHHHHH..',
    'TTTTKHHHHHHHHHHHGG',
    'TTTTKHHHHHHHHHHHGG',
    'TTTTKHHHHHHHHHHHG.',
    '.....KHHHHHHHHHH..',
    '.........MHHHM....',
  ],
  HELI_KEY,
);

export const HELI_L = flip(HELI);

/** The bucket on its long line. */
export const BUCKET = bake(
  [
    'KKKK',
    'KWWK',
    'KWWK',
    '.KK.',
  ],
  { K: '#2a2a34', W: C.blueB },
);

export const HOUSE = bake(
  [
    '............',
    '...RRRRRR...',
    '..RRRRRRRR..',
    '.RRRRRRRRRR.',
    'RRRRRRRRRRRR',
    'KWWWWWWWWWWK',
    'KWWGGWWWDDWK',
    'KWWGGWWWDDWK',
    'KWWWWWWWDDWK',
    'KWWWWWWWWWWK',
    '.KKKKKKKKKK.',
    '............',
  ],
  { R: C.red, W: C.white, G: C.cyanB, D: '#2a2a34', K: '#14141a' },
);

export const HOUSE_BURNT = bake(
  [
    '............',
    '............',
    '...KK..K....',
    '..KKKK.KK...',
    '.KKKKKKKKK..',
    'KKKKKKKKKKK.',
    'KKK.KKK.KKKK',
    'KK..KKK..KKK',
    'KKK.KKKK.KKK',
    'KKKKKKKKKKKK',
    '.KKKKKKKKKK.',
    '............',
  ],
  { K: '#1c1c22' },
);

export const CAR_COLOURS = [C.whiteB, C.cyanB, C.yellowB, C.magentaB, C.white] as const;

export const CARS: Sprite[] = CAR_COLOURS.map((colour) =>
  bake(
    [
      '.CCC.',
      'CCCCC',
      '.K.K.',
    ],
    { C: colour, K: '#14141a' },
  ),
);

/** Autobomba: red cab, water tank, a light bar. */
export const FIRE_ENGINE = bake(
  [
    '..B...B..',
    'RRRRRRRR.',
    'RWWWRRRRR',
    'RWWWRRRRR',
    'RRRRRRRRR',
    '.K..K..K.',
  ],
  { R: C.red, W: C.cyanB, K: '#14141a', B: C.blueB },
);

export const FIRE_ENGINE_LOST = bake(
  [
    '.........',
    'KKKKKKKK.',
    'KK.KKK.KK',
    'K.KKKKKKK',
    'KKKKKK.KK',
    '.K..K..K.',
  ],
  { K: '#20202a' },
);

/** Base pad markings: a yellow square with a cross. */
export const BASE_MARK = bake(
  [
    'YYYYYYYYYYYY',
    'Y..........Y',
    'Y.Y......Y.Y',
    'Y..Y....Y..Y',
    'Y...YYYY...Y',
    'Y...YYYY...Y',
    'Y..Y....Y..Y',
    'Y.Y......Y.Y',
    'Y..........Y',
    'YYYYYYYYYYYY',
  ],
  { Y: C.yellow },
);

/** Assembly point: the white H. */
export const PAD_MARK = bake(
  [
    'W........W',
    'W........W',
    'W........W',
    'WWWWWWWWWW',
    'WWWWWWWWWW',
    'W........W',
    'W........W',
    'W........W',
  ],
  { W: C.whiteB },
);
